// Copyright 2025 Sorama B.V.

import { autoinject, computedFrom, Disposable, observable } from "aurelia-framework";
import { ReportableMeasurement } from "datatypes/ReportableMeasurement";
import { SorXMeasurementTypeEnum } from "datatypes/SoramaWebApi";
import { getEmptyDataForType, SorXLDData, SorXMeasurement } from "datatypes/SorXMeasurement";
import { SorXLDComputationService } from "modules/workflows/Measurement/SpecialMeasurements/Templates/SorXReports/SorXLDOverviewTemplate/SorXLDComputationService";

/**
 * Stores all loaded reports, and selected reports.
 * When edits are made to values, they are applied to the selected reports only
 * Unit changes are applied to all reports
 */
@autoinject
export class ReportExService implements Disposable {
    // Stores all reports
    @observable
    private _reportMap: Map<string, ReportableMeasurement<SorXMeasurement>> = new Map();

    // Selected report IDs
    @observable
    public selectedReportIds: string[] = [];

    private disposables: Disposable[] = [];

    constructor() {}

    public dispose(): void {
        this.disposables.forEach((x) => x.dispose());
    }

    // Computed: all reports in the map that are selected
    @computedFrom("_reportMap", "selectedReportIds")
    public get selectedReports(): ReportableMeasurement<SorXMeasurement>[] {
        return this.selectedReportIds.map((id) => this._reportMap.get(id)).filter((report) => report !== undefined);
    }

    // Add report to service
    public addReport(reportableMeasurement: ReportableMeasurement<SorXMeasurement>, index = -1): void {
        const { id } = reportableMeasurement;

        if (!this._reportMap.has(id)) {
            this._reportMap.set(id, reportableMeasurement);
        }

        this.selectReport(id);
    }

    public updateReport(report: ReportableMeasurement<SorXMeasurement>) {
        this._reportMap.set(report.id, report);
    }

    public removeReport(id: string): void {
        this._reportMap.delete(id);
        this.deselectReport(id);
    }

    public removeAllReports(): void {
        this._reportMap.clear();
        this.clearSelection();
    }

    public getAllReports(): ReportableMeasurement<SorXMeasurement>[] {
        return Array.from(this._reportMap.values());
    }

    public selectReport(id: string): void {
        if (!this.selectedReportIds.includes(id)) {
            this.selectedReportIds.push(id);
        }
    }

    public deselectReport(id: string): void {
        this.selectedReportIds = this.selectedReportIds.filter((reportId) => reportId !== id);
    }

    public clearSelection(): void {
        this.selectedReportIds = [];
    }

    @computedFrom("selectedReports")
    public get hasNoMeasurementSelected(): boolean {
        return this.selectedReports.length === 0;
    }

    @computedFrom("selectedReports")
    public get hasMixedMeasurementTypesSelected(): boolean {
        const selectedReports = this.selectedReports;
        if (selectedReports.length <= 1) return false;

        const firstType = selectedReports[0]?.type;
        return selectedReports.some((report) => report.type !== firstType);
    }

    @computedFrom("selectedReports")
    public get canEditOrGenerateReport(): boolean {
        return !this.hasNoMeasurementSelected && !this.hasMixedMeasurementTypesSelected;
    }

    public getSingleOrDefault(): ReportableMeasurement<SorXMeasurement> | null {
        const all = this.selectedReports;
        if (all.length === 0) return null;
        if (all.length === 1) return all[0];

        const last = all[all.length - 1];

        if (this._reportMap.has(last.id)) return this._reportMap.get(last.id);

        return getEmptyDataForType(last.type as SorXMeasurementTypeEnum);
    }

    /**
     * Recalculates results for all selected reports.
     * Specifically updates the tagLeakCost for SorXLD reports based on their data.
     */
    public recalculateResults(): void {
        for (const report of this.selectedReports) {
            if (report.type === "SORXLD") {
                (report.data as SorXLDData).tagLeakCost = SorXLDComputationService.estimatedLeakCosts(
                    report as ReportableMeasurement<SorXLDData>
                );
            }
        }
    }
}
